import { extendTheme } from "@chakra-ui/react";

import colors from "./colors";
import * as Components from "./components";
import { scale } from "./scale";
import { typography } from "./typography";

export const theme = extendTheme({
  config: {
    cssVarPrefix: "theme",
  },
  styles: {
    global: {
      html: {
        fontSize: "lg",
      },
      body: {
        fontFamily: "body",
        color: "text.primary",
        width: "100%",
        height: "100%",
        textRendering: "optimizeLegibility",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
      },
      // Thin scrollbars everywhere (Firefox + WebKit).
      "*": {
        scrollbarWidth: "thin",
        scrollbarColor: "var(--theme-colors-gray-300) transparent",
      },
      "*::-webkit-scrollbar": {
        width: "8px",
        height: "8px",
      },
      "*::-webkit-scrollbar-thumb": {
        backgroundColor: "var(--theme-colors-gray-300)",
        borderRadius: "9999px",
      },
      "*::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
      },
    },
  },
  // Space variables that are used for setting margin, padding, gap, etc.
  space: { ...scale },
  // Size variables that are used for setting width, height, boxSize, etc.
  sizes: {
    ...scale,
    // Global header height — reference as var(--theme-sizes-header) in sticky
    // offsets and height calcs (topbar, left nav, right TOC).
    header: "64px",
    max: "max-content",
    min: "min-content",
    full: "100%",
    container: {
      sm: "640px",
      md: "768px",
      lg: "1024px",
      xl: "1280px",
    },
  },
  radii: {
    xs: "2px",
    sm: "4px",
    md: "6px",
    lg: "8px",
    xl: "12px",
    base: "6px",
    full: "9999px",
  },
  shadows: {
    // Synced from hightouch-ui (source/theme/foundations/shadows.ts). Shadows are
    // reserved for floating UI (lg = popover, md = drawer/modal, xs = small controls);
    // cards and containers use borders, not resting shadows.
    none: "none",
    xs: "0 0 0 1px rgba(0, 0, 0, 0.08) inset, lch(0 0 0 / 0.020) 0px 3px 6px -2px, lch(0 0 0 / 0.040) 0px 2px 1px -1px",
    sm: "0px 4px 8px 0px rgba(0, 0, 0, 0.04), 0px 0px 2px 0px rgba(0, 0, 0, 0.06), 0px 0px 1px 0px rgba(0, 0, 0, 0.12)",
    md: "0px 0px 1px 0px rgba(0, 0, 0, 0.20), 0px 1px 6px 0px rgba(0, 0, 0, 0.04), 0px 12px 20px -2px rgba(0, 0, 0, 0.06)",
    lg: "0px 24px 32px 0px rgba(0, 0, 0, 0.04), 0px 16px 24px 0px rgba(0, 0, 0, 0.04), 0px 4px 8px 0px rgba(0, 0, 0, 0.04), 0px 0px 1px 0px rgba(0, 0, 0, 0.20)",
    buttonShadow:
      "0px 0px 0px 1px rgba(0, 0, 0, 0.32) inset, 0px 0px 0px 1.5px rgba(255, 255, 255, 0.16) inset, lch(0 0 0 / 0.022) 0px 3px 6px -2px, lch(0 0 0 / 0.044) 0px 1px 1px",
    buttonShadowLight:
      "lch(0 0 0 / 0.022) 0px 3px 6px -2px, lch(0 0 0 / 0.044) 0px 1px 1px",
    outline:
      "0px 0px 0px 1px #ffffff, 0px 0px 0px 2px var(--theme-colors-primary-background), 0px 0px 0px 3px var(--theme-colors-primary-border)",
    outlineRed:
      "0px 0px 0px 2px #ffffff, 0px 0px 0px 3px var(--theme-colors-danger-base)",
  },
  colors,
  components: Components,
  ...typography,
});
